import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';

import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { StudentService } from '../../services/student.service';
import { VolunteerService } from '../../services/volunteer.service';
import { AccreditationService } from '../../services/accreditation.service';
import { Student, StatusHistoryEntry } from '../../interfaces/student.interfaces';
import { CustomSelectComponent } from '../ui/custom-select/custom-select.component';
import { CustomDatepickerComponent } from '../ui/custom-datepicker/custom-datepicker.component';
import { FolioPillComponent } from '../ui/folio-pill/folio-pill.component';
import { InactivateVolunteerModalComponent } from '../shared/inactivate-volunteer-modal/inactivate-volunteer-modal.component';


export type StudentDocumentField =
  | 'cartaAceptacion'
  | 'cartaTermino'
  | 'constanciaPlatica'
  | 'reporteActividades'
  | 'ine'
  | 'cv'
  | 'comprobante';


@Component({
  selector: 'app-student-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    CustomSelectComponent,
    CustomDatepickerComponent,
    FolioPillComponent,
    InactivateVolunteerModalComponent,
  ],
  templateUrl: './student-detail.html',
  styleUrl: './student-detail.scss',
})
export class StudentDetail implements OnInit, OnDestroy {

  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private studentService =
    inject(StudentService);

  private volunteerService =
    inject(VolunteerService);

  private accreditationService =
    inject(AccreditationService);

  private cdr =
    inject(ChangeDetectorRef);

  private fb = inject(FormBuilder);

  expandedCommentId: string | null = null;

  studentId: string | null = null;

  student: Student | null = null;

  isLoading = true;
  isAccepting = false;
  isRejecting = false;
  statusFeedback = '';
  showAccreditationModal = false;
  approvingAccreditationId: string | null = null;
  rejectingAccreditationId: string | null = null;
  isFolioCopied = false;
  private folioCopyTimeout: any = null;

  showInactivateModal = false;

  acceptStudent(id?: string): void {
    const targetId = id || this.studentId;
    if (!targetId) return;

    const comments = window.prompt(
      `Mensaje o nota de bienvenida para ${this.student?.fullName || this.student?.folio || 'el estudiante'} (se incluirá en el correo oficial y en su expediente):`,
      ''
    );
    if (comments === null) {
      // El administrador canceló la aceptación
      return;
    }

    this.isAccepting = true;
    this.statusFeedback = '';
    this.studentService.acceptStudent(targetId, comments.trim()).subscribe({
      next: (updated) => {
        this.isAccepting = false;
        this.student = updated;
        this.statusFeedback = '¡Estudiante aceptado exitosamente! Se ha emitido su Carta de Aceptación oficial y se le ha enviado por correo.';
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isAccepting = false;
        this.statusFeedback = 'Error al aceptar al estudiante: ' + (err?.error?.message || 'Error en el servidor');
        this.cdr.detectChanges();
      },
    });
  }

  rejectStudent(id?: string): void {
    const targetId = id || this.studentId;
    if (!targetId) return;
    const comments = window.prompt(
      'Motivo u observaciones para el rechazo del expediente:',
      'Documentación incompleta o no legible. Favor de corregir en el registro.'
    );
    if (comments === null) return;
    this.isRejecting = true;
    this.statusFeedback = '';
    this.studentService.rejectStudent(targetId, comments.trim()).subscribe({
      next: (updated) => {
        this.isRejecting = false;
        this.student = updated;
        this.statusFeedback = 'El expediente ha sido marcado como rechazado y se enviaron las observaciones por correo.';
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isRejecting = false;
        this.statusFeedback = 'Error al rechazar al estudiante: ' + (err?.error?.message || 'Error en el servidor');
        this.cdr.detectChanges();
      },
    });
  }

  openInactivateModal(): void {
    this.showInactivateModal = true;
  }

  closeInactivateModal(): void {
    this.showInactivateModal = false;
  }

  handleInactivateConfirmed(event: { comments: string }): void {
    if (!this.studentId) return;
    this.studentService.updateStudentAccountStatus(
      this.studentId,
      'inactive',
      'administrative_decision',
      event.comments,
    ).subscribe({
      next: (updated) => {
        this.student = updated;
        this.closeInactivateModal();
        this.statusFeedback = 'Cuenta inactivada exitosamente por decisión administrativa.';
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error al inactivar estudiante:', err);
        this.statusFeedback = 'Error al inactivar cuenta: ' + (err?.error?.message || 'Error en el servidor');
        this.closeInactivateModal();
        this.cdr.detectChanges();
      },
    });
  }

  getFriendlyReason(reason?: string): string {
    if (!reason) return 'Decisión administrativa institucional';
    const map: Record<string, string> = {
      initial_signup: 'Registro inicial completado',
      admin_approval: 'Aprobación oficial de expediente',
      admin_rejection: 'Rechazo con observaciones de Coordinación',
      hours_completed: 'Conclusión satisfactoria de 480 horas de servicio',
      administrative_decision: 'Baja administrativa institucional',
      re_registration: 'Re-registro y corrección de expediente',
    };
    return map[reason] || reason;
  }
  accreditations: any[] = [];
  accActiveTab: 'acreditaciones' | 'actividades' | 'todas' = 'todas';
  setAccTab(tab: 'acreditaciones' | 'actividades' | 'todas'): void {
    this.accActiveTab = tab;
  }
  expandedAccreditationId: string | null = null;
  selectedAccreditationType = '';
  protected readonly Math = Math;
  eventDate = '';
  eventType = 'GENERAL';
  hoursPerformed: number | null = null;
  identificationQuantity: number | null = null;
  sterilizationQuantity: number | null = null;
  adoptionQuantity: number | null = null;
  adoptionModuleQuantity: number | null = null;
  talkParticipantsCount: number | null = null;
  campaignAnimalsEnrolled: number | null = null;
  campaignInfrastructureAvailable = false;
  mediaDate = '';
  socialMediaReactionsCount: number | null = null;
  donationAmount: number | null = null;
  administrativeHoursAccredited: number | null = null;
  evidenceUrl = '';

  accreditationTypeOptions = [
    { value: 'EVENT', label: 'Evento ABAQ' },
    { value: 'INDUCTION', label: 'Inducción' },
    { value: 'IDENTIFICATION', label: 'Placa de identificación' },
    { value: 'STERILIZATION', label: 'Esterilización' },
    { value: 'ADOPTION', label: 'Adopción' },
    { value: 'ADOPTION_MODULE', label: 'Módulo de adopción' },
    { value: 'TALK', label: 'Plática' },
    { value: 'CAMPAIGN', label: 'Campaña' },
    { value: 'MEDIA', label: 'Difusión en medios' },
    { value: 'SOCIAL_MEDIA', label: 'Redes sociales' },
    { value: 'DONATION', label: 'Recaudación de fondos' },
    { value: 'ADMINISTRATIVE', label: 'Actividad administrativa' },
  ];

  eventTypeOptions = [
    { value: 'GENERAL', label: 'Evento general' },
    { value: 'SHELTER_CLEANING', label: 'Limpieza de refugio' },
  ];

  campaignInfrastructureOptions = [
    { value: false, label: 'No' },
    { value: true, label: 'Sí' },
  ];

  openAccreditationModal(): void {
    console.log('🔵 CLICK EN NUEVA ACREDITACIÓN');
    this.showAccreditationModal = true;
    this.selectedAccreditationType = '';
  }
  
  closeAccreditationModal(): void {
    this.showAccreditationModal = false;
  }

  createAccreditation(): void {
    if (!this.studentId) {
      return;
    }
  
    if (!this.selectedAccreditationType) {
      return;
    }
  
    if (
      this.selectedAccreditationType === 'MEDIA'
        ? !this.mediaDate
        : !this.eventDate
    ) {
      return;
    }
  
    if (
      this.selectedAccreditationType === 'EVENT' &&
      (!this.eventType || this.hoursPerformed === null)
    ) {
      return;
    }

    if (
      this.selectedAccreditationType === 'CAMPAIGN' &&
      this.campaignAnimalsEnrolled === null
    ) {
      return;
    }

    if (
      this.selectedAccreditationType === 'ADMINISTRATIVE' &&
      this.administrativeHoursAccredited === null
    ) {
      return;
    }
  
    const accreditation = {
      studentRef: this.studentId,
      type: this.selectedAccreditationType,
      evidenceUrl: this.evidenceUrl,
      date:
      this.selectedAccreditationType === 'MEDIA'
    ? this.mediaDate
    : this.eventDate,
      eventType:
        this.selectedAccreditationType === 'EVENT'
          ? this.eventType
          : undefined,
      hoursPerformed:
        this.selectedAccreditationType === 'EVENT'
          ? this.hoursPerformed ?? 0
          : undefined,
        quantity:
          this.selectedAccreditationType === 'IDENTIFICATION'
            ? this.identificationQuantity ?? 0
            : this.selectedAccreditationType === 'STERILIZATION'
              ? this.sterilizationQuantity ?? 0
              : this.selectedAccreditationType === 'ADOPTION'
                ? this.adoptionQuantity ?? 0
                : this.selectedAccreditationType === 'ADOPTION_MODULE'
                  ? this.adoptionModuleQuantity ?? 0
                  : undefined,
      
        participantsCount:
                  this.selectedAccreditationType === 'TALK'
                    ? this.talkParticipantsCount ?? 0
                    : undefined,
        animalsEnrolled:
                  this.selectedAccreditationType === 'CAMPAIGN'
                    ? this.campaignAnimalsEnrolled ?? 0
                    : undefined,

        infrastructureAvailable:
                  this.selectedAccreditationType === 'CAMPAIGN'
                    ? this.campaignInfrastructureAvailable
                    : undefined,
        
        reactionsCount:
                  this.selectedAccreditationType === 'SOCIAL_MEDIA'
                    ? this.socialMediaReactionsCount ?? 0
                    : undefined,
        
        donationAmount:
                  this.selectedAccreditationType === 'DONATION'
                    ? this.donationAmount ?? 0
                    : undefined,
        
        hoursAccredited:
                  this.selectedAccreditationType === 'ADMINISTRATIVE'
                    ? this.administrativeHoursAccredited ?? 0
                    : undefined,
    };
  
    console.log('🔵 ENVIANDO ACREDITACIÓN:', accreditation);
  
    this.accreditationService
      .createAccreditation(accreditation)
      .subscribe({
        next: (response: any) => {
          console.log('🟢 ACREDITACIÓN CREADA:', response);
        
          const newAccreditation =
            response.data.document;
        
          this.accreditations = [
            newAccreditation,
            ...this.accreditations,
          ];
        
          this.showAccreditationModal = false;
        
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('🔴 ERROR CREANDO ACREDITACIÓN:', error);
        },
      });
  }

  approveAccreditation(id: string): void {
    this.approvingAccreditationId = id;
  
    this.accreditationService
      .approveAccreditation(id)
      .subscribe({
        next: (response: any) => {
          console.log('🟢 ACREDITACIÓN APROBADA:', response);
  
          const updatedAccreditation =
            response.data.document;
  
          const index =
            this.accreditations.findIndex(
              (item) => item._id === id,
            );
  
          if (index !== -1) {
            this.accreditations[index] =
              updatedAccreditation;
          }
  
          if (this.student) {
            this.student.horasAcreditadas =
              (this.student.horasAcreditadas ?? 0) +
              (updatedAccreditation.hoursAccredited ?? 0);
  
            this.student.horasPendientes =
              Math.max(
                0,
                this.student.horasCubrir -
                  this.student.horasAcreditadas,
              );
          }
  
          this.approvingAccreditationId = null;
  
          this.cdr.detectChanges();
        },
  
        error: (error) => {
          console.error(
            '🔴 ERROR APROBANDO ACREDITACIÓN:',
            error,
          );
  
          this.approvingAccreditationId = null;
  
          this.cdr.detectChanges();
        },
      });
  }

  rejectAccreditation(id: string): void {
    this.rejectingAccreditationId = id;
  
    this.accreditationService
      .rejectAccreditation(id)
      .subscribe({
        next: (response) => {
          console.log(
            '🟢 ACREDITACIÓN RECHAZADA:',
            response,
          );
  
          const accreditation = this.accreditations.find(
            (item) => item._id === id,
          );
  
          if (accreditation) {
            accreditation.status = 'rejected';
          }
  
          this.rejectingAccreditationId = null;
  
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error(
            '🔴 ERROR RECHAZANDO ACREDITACIÓN:',
            error,
          );
  
          this.rejectingAccreditationId = null;
  
          this.cdr.detectChanges();
        },
      });
  }

  toggleAccreditationDetails(id: string): void {
    this.expandedAccreditationId =
      this.expandedAccreditationId === id
        ? null
        : id;
  }

  isEditing = false;
  isSaving = false;
  profileError: string | null = null;
  profileSuccess: string | null = null;

studentForm = this.fb.group({
  fullName: ['', Validators.required],
  matricula: ['', Validators.required],
  mail: ['', Validators.email],
  schoolMail: ['', [Validators.required, Validators.email]],
  phone: ['', Validators.required],
  comoSupisteDeAbaq: [''],
  bioPersonal: [''],
  school: ['', Validators.required],
  career: ['', Validators.required],
  auditorName: [''],
  semester: [1, [Validators.required, Validators.min(1), Validators.max(12)]],
  birthDate: ['', Validators.required],
  desiredHours: [0, [Validators.required, Validators.min(1)]],
  beginDate: ['', Validators.required],
  endDate: [''],

  facebookUsuario: [''],
  instagramUsuario: [''],
  tiktokUsuario: [''],
  xUsuario: [''],
  youtubeUsuario: [''],
  linkedinUsuario: [''],
});


  documentAction: {
    field: StudentDocumentField;
    type: 'upload' | 'delete';
  } | null = null;

  documentError: string | null = null;
  documentActionSuccess: string | null = null;

  /* =========================================
     MODAL DE ENLACES EN LA NUBE
  ========================================== */
  showDocumentLinkModal = false;
  editingDocumentField: StudentDocumentField | null = null;
  editingDocumentTitle = '';
  editingDocumentSubtitle = '';
  editingDocumentUrl = '';
  documentLinkError: string | null = null;
  isSavingDocumentLink = false;


  /* =========================================
     INICIO
  ========================================== */

  ngOnInit(): void {

    this.studentId =
      this.route.snapshot.paramMap.get('id');


    console.log(
      'ID DEL ESTUDIANTE:',
      this.studentId,
    );


    if (!this.studentId) {

      this.isLoading = false;

      return;
    }


    this.studentService
      .getStudentById(this.studentId)
      .subscribe({
        next: (student) => {
          if ((student as any)?.volunteerType === 'GENERAL' || (student as any)?.tipoVoluntario === 'GENERAL') {
            this.router.navigate(['/volunteer-detail', this.studentId], { replaceUrl: true });
            return;
          }

          this.student = student;
          this.loadStudentForm(student);

        this.accreditationService
            .getStudentAccreditations(this.studentId!)
            .subscribe({
              next: (response: any) => {
                console.log(
                  '🟢 ACREDITACIONES DEL ESTUDIANTE:',
                  response,
                );

                this.accreditations =
                  response.data.documents;

                this.cdr.detectChanges();
              },
              error: (error) => {
                console.error(
                  '🔴 ERROR CARGANDO ACREDITACIONES:',
                  error,
                );

                this.accreditations = [];

                this.cdr.detectChanges();
              },
            });
  

        console.log(
          'DOCUMENTOS DEL ESTUDIANTE:',
          {
            id: student.id,
            nombre: student.fullName,
            cartaAceptacion:
              student.cartaAceptacion,
            constanciaPlatica:
              student.constanciaPlatica,
            cartaTermino:
              student.cartaTermino,
          },
        );


        console.log(
          'STUDENT DETAIL DATA:',
          student,
        );


        this.isLoading = false;

        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        console.error(
          'ERROR CARGANDO ESTUDIANTE:',
          error,
        );

        // Fallback: Si no es estudiante de servicio social, verificar si es voluntario general
        if (this.studentId) {
          this.volunteerService.getVolunteer(this.studentId).subscribe({
            next: (vol) => {
              if (vol && (vol.id || (vol as any)?._id)) {
                this.router.navigate(['/volunteer-detail', this.studentId], { replaceUrl: true });
                return;
              }
              this.isLoading = false;
              this.cdr.detectChanges();
            },
            error: () => {
              this.isLoading = false;
              this.cdr.detectChanges();
            },
          });
          return;
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  ngOnDestroy(): void {
    if (this.folioCopyTimeout) {
      clearTimeout(this.folioCopyTimeout);
    }
  }


  private formatDateForInput(date: Date | null | undefined): string {
    if (!date) {
      return '';
    }
  
    const value = new Date(date);
  
    if (Number.isNaN(value.getTime())) {
      return '';
    }
  
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
  
    return `${year}-${month}-${day}`;
  }
  
  
  private loadStudentForm(student: Student): void {
    const period = student.periods?.[0];
  
    this.studentForm.reset({
      fullName: student.fullName ?? '',
      matricula: student.matricula ?? '',
      mail: student.mail ?? '',
      schoolMail: student.schoolMail ?? '',
      phone: student.phone ?? '',
      comoSupisteDeAbaq: student.comoSupisteDeAbaq ?? '',
      bioPersonal: student.bioPersonal ?? '',
      school: student.school ?? '',
      career: student.career ?? '',
      auditorName: student.auditorName ?? '',
      semester: typeof student.semester === 'number' ? student.semester : (Number(String(student.semester).match(/\d+/)?.[0]) || 1),
      birthDate: this.formatDateForInput(student.birthDate),
      desiredHours: period?.desiredHours ?? 0,
      beginDate: this.formatDateForInput(period?.begin),
      endDate: this.formatDateForInput(period?.end),
      facebookUsuario: student.socialMedia?.facebook ?? '',
      instagramUsuario: student.socialMedia?.instagram ?? '',
      tiktokUsuario: student.socialMedia?.tiktok ?? '',
      xUsuario: student.socialMedia?.x ?? '',
      youtubeUsuario: student.socialMedia?.youtube ?? '',
      linkedinUsuario: student.socialMedia?.linkedin ?? '',
    });
  }
  
  
  startEdit(): void {
    if (!this.student) {
      return;
    }
  
    this.profileError = null;
    this.profileSuccess = null;
  
    this.loadStudentForm(this.student);
  
    this.isEditing = true;
  }
  
  
  cancelEdit(): void {
    if (this.student) {
      this.loadStudentForm(this.student);
    }
  
    this.isEditing = false;
    this.profileError = null;
  }
  
  
  saveStudent(): void {
    if (
      !this.student ||
      !this.studentId ||
      this.studentForm.invalid
    ) {
      this.studentForm.markAllAsTouched();
      return;
    }
  
    const values = this.studentForm.getRawValue();

      console.log('🔎 VALORES DEL FORMULARIO:', values);
      console.log(
        '🔎 COMO SUPISTE DESDE FORM:',
        values.comoSupisteDeAbaq
      );

this.isSaving = true;
    this.profileError = null;
    this.profileSuccess = null;
  
    const currentPeriod = this.student.periods?.[0];
  
    const updatedStudent: Student = {
      ...this.student,
  
      fullName: values.fullName ?? '',
      matricula: values.matricula ?? '',
      mail: values.mail ?? '',
      schoolMail: values.schoolMail ?? '',
      phone: values.phone ?? '',
      comoSupisteDeAbaq: values.comoSupisteDeAbaq ?? '',
      bioPersonal: values.bioPersonal ?? '',
      school: values.school ?? '',
      career: values.career ?? '',
      auditorName: values.auditorName ?? '',
      semester: values.semester ?? 1,
  
      socialMedia: {
        facebook: values.facebookUsuario ?? '',
        instagram: values.instagramUsuario ?? '',
        tiktok: values.tiktokUsuario ?? '',
        x: values.xUsuario ?? '',
        youtube: values.youtubeUsuario ?? '',
        linkedin: values.linkedinUsuario ?? '',
      },

      birthDate: values.birthDate
        ? new Date(`${values.birthDate}T00:00:00`)
        : this.student.birthDate,
  
      periods: [
        {
          desiredHours: values.desiredHours ?? 0,
          validatedHours: currentPeriod?.validatedHours ?? 0,
  
          begin: values.beginDate
            ? new Date(`${values.beginDate}T00:00:00`)
            : currentPeriod?.begin ?? new Date(),
  
          end: values.endDate
            ? new Date(`${values.endDate}T00:00:00`)
            : currentPeriod?.end ?? new Date(),
        },
      ],
    };
  
    this.studentService
      .updateStudent(
        this.studentId,
        updatedStudent,
      )
      .subscribe({
        next: (student) => {
          this.student = student;
  
          this.loadStudentForm(student);

          this.isSaving = false;
          this.isEditing = false;
  
          this.profileSuccess =
            'La información del estudiante se actualizó correctamente.';
  
          this.cdr.detectChanges();
        },
  
        error: (error: unknown) => {
          console.error(
            'ERROR ACTUALIZANDO ESTUDIANTE:',
            error,
          );
  
          this.isSaving = false;
  
          this.profileError =
            'No se pudo actualizar la información del estudiante.';
  
          this.cdr.detectChanges();
        },
      });
  }

  /* =========================================
     VALIDAR ENLACE EN LA NUBE O CERTIFICADO
  ========================================== */
  hasValidDocument(fileUrl: string | null | undefined): boolean {
    if (!fileUrl || fileUrl === 'pending') {
      return false;
    }
    const trimmed = fileUrl.trim();
    return (
      trimmed.length > 0 &&
      (trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('/'))
    );
  }

  getDocumentValue(field: StudentDocumentField): string {
    if (!this.student) return '';
    if (field === 'ine' || field === 'cv' || field === 'comprobante') {
      return this.student.cloudUrls?.[field] || '';
    }
    const val = (this.student as any)[field];
    return val && val !== 'pending' ? val : '';
  }

  /* =========================================
     ABRIR ENLACE DE NUBE O CERTIFICADO
  ========================================== */
  openDocument(fileUrl: string | null | undefined): void {
    if (!fileUrl || fileUrl === 'pending') {
      return;
    }
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }

  /* =========================================
     GESTIÓN DE MODAL PARA REGISTRAR / EDITAR ENLACE
  ========================================== */
  openLinkModal(
    field: StudentDocumentField,
    title: string,
    subtitle: string,
    currentUrl?: string,
  ): void {
    this.editingDocumentField = field;
    this.editingDocumentTitle = title;
    this.editingDocumentSubtitle = subtitle;
    this.editingDocumentUrl =
      currentUrl && currentUrl !== 'pending' ? currentUrl : '';
    this.documentLinkError = null;
    this.showDocumentLinkModal = true;
  }

  openEditDocumentLinkModal(field: StudentDocumentField): void {
    const titles: Record<StudentDocumentField, { title: string; subtitle: string }> = {
      cartaAceptacion: { title: 'Carta de aceptación', subtitle: 'Enlace de la carta de aceptación del estudiante.' },
      constanciaPlatica: { title: 'Constancia de plática', subtitle: 'Enlace de la constancia de asistencia a la plática institucional.' },
      cartaTermino: { title: 'Carta de término', subtitle: 'Enlace del certificado de término o liberación de servicio social.' },
      reporteActividades: { title: 'Reporte de actividades', subtitle: 'Enlace del reporte de actividades en la nube.' },
      ine: { title: 'Identificación Oficial (INE)', subtitle: 'Enlace de la credencial oficial o escolar en la nube.' },
      cv: { title: 'Currículum Vitae (CV)', subtitle: 'Enlace del currículum o semblanza en la nube.' },
      comprobante: { title: 'Comprobante de Estudios', subtitle: 'Enlace del comprobante de estudios o domicilio en la nube.' },
    };
    const config = titles[field] || { title: 'Documento', subtitle: 'Enlace en la nube' };
    this.openLinkModal(field, config.title, config.subtitle, this.getDocumentValue(field));
  }

  closeLinkModal(): void {
    this.showDocumentLinkModal = false;
    this.editingDocumentField = null;
    this.editingDocumentUrl = '';
    this.documentLinkError = null;
  }

  testLink(url: string): void {
    if (!url || !url.trim()) return;
    const trimmed = url.trim();
    const finalUrl =
      trimmed.startsWith('http://') || trimmed.startsWith('https://')
        ? trimmed
        : `https://${trimmed}`;
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  }

  saveDocumentLink(): void {
    if (!this.studentId || !this.editingDocumentField) {
      return;
    }

    const trimmed = (this.editingDocumentUrl || '').trim();
    if (!trimmed) {
      this.documentLinkError = 'Por favor ingresa un enlace URL válido.';
      return;
    }

    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      this.documentLinkError =
        'El enlace debe comenzar con https:// o http:// (ej. Google Drive o OneDrive).';
      return;
    }

    this.isSavingDocumentLink = true;
    this.documentLinkError = null;

    let payload: Record<string, any>;
    const field = this.editingDocumentField;

    if (field === 'ine' || field === 'cv' || field === 'comprobante') {
      const currentCloud = this.student?.cloudUrls || {};
      payload = {
        cloudUrls: {
          ...currentCloud,
          [field]: trimmed,
        },
      };
    } else {
      payload = {
        [field]: trimmed,
      };
    }

    this.studentService.updateStudentDocuments(this.studentId, payload).subscribe({
      next: (updatedStudent) => {
        this.student = updatedStudent;
        this.isSavingDocumentLink = false;
        this.documentActionSuccess = `Enlace para "${this.editingDocumentTitle}" guardado exitosamente.`;
        this.closeLinkModal();
        this.cdr.detectChanges();

        setTimeout(() => {
          this.documentActionSuccess = null;
          this.cdr.detectChanges();
        }, 4000);
      },
      error: (err) => {
        console.error('Error al guardar enlace de documento:', err);
        this.isSavingDocumentLink = false;
        this.documentLinkError =
          'No se pudo guardar el enlace. Intenta de nuevo.';
        this.cdr.detectChanges();
      },
    });
  }

  /* =========================================
     ELIMINAR ENLACE DE DOCUMENTO
  ========================================== */
  deleteDocumentLink(
    field: StudentDocumentField,
    title: string,
  ): void {
    if (!this.studentId || !this.student) {
      return;
    }

    const confirmed = window.confirm(
      `¿Seguro que deseas retirar el enlace de "${title}" del expediente?`,
    );

    if (!confirmed) {
      return;
    }

    this.documentError = null;

    let payload: Record<string, any>;
    if (field === 'ine' || field === 'cv' || field === 'comprobante') {
      const currentCloud = this.student.cloudUrls || {};
      payload = {
        cloudUrls: {
          ...currentCloud,
          [field]: '',
        },
      };
    } else {
      payload = {
        [field]: 'pending',
      };
    }

    this.studentService.updateStudentDocuments(this.studentId, payload).subscribe({
      next: (updatedStudent) => {
        this.student = updatedStudent;
        this.documentActionSuccess = `Se retiró el enlace de "${title}".`;
        this.cdr.detectChanges();

        setTimeout(() => {
          this.documentActionSuccess = null;
          this.cdr.detectChanges();
        }, 4000);
      },
      error: (err) => {
        console.error('Error al retirar enlace de documento:', err);
        this.documentError = 'No se pudo retirar el enlace del expediente.';
        this.cdr.detectChanges();
      },
    });
  }

  isUploading(field: StudentDocumentField): boolean {
    return false;
  }

  isDeleting(field: StudentDocumentField): boolean {
    return false;
  }

  toggleComment(activityRef: string): void {
  this.expandedCommentId =
    this.expandedCommentId === activityRef
      ? null
      : activityRef;
}

  /* =========================================
     BITÁCORA DE ESTATUS (Fase C4.1)
  ========================================== */
  get lastStatusChange(): StatusHistoryEntry | null {
    const history = this.student?.statusHistory;
    if (!history || history.length === 0) {
      return null;
    }
    return [...history].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    })[0];
  }

  get reflectionsList() {
    const refs = this.student?.onboardingData?.reflections || {};
    const modules = [
      { id: 'modulo-1', name: 'Módulo 1: Historia, Misión y Visión de ABAQ' },
      { id: 'modulo-2', name: 'Módulo 2: Valores y Cultura del Voluntariado' },
      { id: 'modulo-3', name: 'Módulo 3: Bienestar y Rescate Animal' },
      { id: 'modulo-4', name: 'Módulo 4: Protocolos de Seguridad y Convivencia' },
      { id: 'modulo-5', name: 'Módulo 5: Adopciones Responsables y Campañas' },
      { id: 'modulo-6', name: 'Módulo 6: Organización de Eventos y Difusión' },
    ];
    return modules.map((m) => ({
      ...m,
      data: refs[m.id] || null,
    }));
  }

  hasAnyReflections(): boolean {
    const refs = this.student?.onboardingData?.reflections;
    return !!refs && Object.keys(refs).length > 0;
  }

  getActivityTitle(act: string): string {
    if (!act) return '';
    return act.replace(/\s*\((?:Acredita|Horas):\s*([^)]+)\)\s*$/i, '').trim();
  }

  getActivityHours(act: string): string | null {
    if (!act) return null;
    const match = act.match(/\((?:Acredita|Horas):\s*([^)]+)\)\s*$/i);
    return match ? match[1].trim() : null;
  }

  legalDocs: Record<string, { title: string; url: string }> = {
    reglamento: {
      title: 'Reglamento General Interno',
      url: '',
    },
    privacidad: {
      title: 'Aviso de Privacidad Integral',
      url: '',
    },
    etica: {
      title: 'Código de Ética y Conducta',
      url: '',
    },
    incognito: {
      title: 'Permiso de Difusión Institucional',
      url: '',
    },
    consentimiento: {
      title: 'Consentimiento Voluntario Expreso',
      url: '',
    },
  };

  getLegalDocUrl(docKey: string): string {
    const doc = this.legalDocs[docKey];
    return doc?.url || '#';
  }

  openLegalDoc(docKey: string, event?: Event): void {
    const doc = this.legalDocs[docKey];
    const url = doc?.url;
    if (url && url.startsWith('http')) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    const win = window.open('', '_blank');
    if (win) {
      const docTitle = doc?.title || 'Documento Normativo Institucional';
      win.document.write(`
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${docTitle} · ABAQ</title>
          <style>
            body {
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              background: #f8fafc;
              color: #1e293b;
              padding: 24px;
              box-sizing: border-box;
            }
            .card {
              max-width: 540px;
              width: 100%;
              padding: 40px 32px;
              background: #ffffff;
              border-radius: 20px;
              border: 1px solid #e2e8f0;
              box-shadow: 0 16px 36px rgba(36, 54, 111, 0.08);
              text-align: center;
            }
            .badge {
              display: inline-block;
              background: #ecfdf5;
              color: #065f46;
              border: 1px solid #a7f3d0;
              padding: 5px 14px;
              border-radius: 999px;
              font-size: 11.5px;
              font-weight: 800;
              letter-spacing: 0.5px;
              margin-bottom: 20px;
              text-transform: uppercase;
            }
            h1 {
              color: #24366f;
              margin: 0 0 12px;
              font-size: 22px;
              font-weight: 800;
            }
            p {
              color: #64748b;
              line-height: 1.6;
              font-size: 14.5px;
              margin: 0 0 24px;
            }
            .info-box {
              background: #f1f5f9;
              border-radius: 12px;
              padding: 16px;
              font-size: 13px;
              color: #334155;
              border: 1px solid #cbd5e1;
            }
          </style>
        </head>
        <body>
          <div class="card">
            <span class="badge">✓ Validado en Expediente Digital</span>
            <h1>${docTitle}</h1>
            <p>Este documento normativo fue revisado y aceptado expresamente durante el proceso de registro institucional en ABAQ Querétaro.</p>
            <div class="info-box">
              📄 La versión digital descargable para consulta pública se encuentra en preparación y estará vinculada a este enlace próximamente.
            </div>
          </div>
        </body>
        </html>
      `);
      win.document.close();
    }
  }

  get opportunitiesCount(): number {
    return this.accreditations?.length || 0;
  }

  get activitiesCount(): number {
    return this.student?.assignedActivities?.length || 0;
  }

  get totalStatsCount(): number {
    return this.opportunitiesCount + this.activitiesCount;
  }

  copyFolioToClipboard(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.student?.folio) return;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.student.folio)
        .then(() => {
          this.triggerFolioCopiedState();
        })
        .catch(() => {
          this.fallbackCopyText(this.student!.folio!);
        });
    } else {
      this.fallbackCopyText(this.student.folio);
    }
  }

  private triggerFolioCopiedState(): void {
    this.isFolioCopied = true;
    this.cdr.detectChanges();
    if (this.folioCopyTimeout) {
      clearTimeout(this.folioCopyTimeout);
    }
    this.folioCopyTimeout = setTimeout(() => {
      this.isFolioCopied = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  private fallbackCopyText(text: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.triggerFolioCopiedState();
    } catch (e) {
      console.error('No se pudo copiar el folio:', e);
    }
  }

}